import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  CurrencyDollarIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  TruckIcon,
  ChartBarIcon,
  ExclamationCircleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArchiveBoxIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import api from '../services/api';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import DataTable from '../components/DataTable';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [recentConsignments, setRecentConsignments] = useState([]);
  const [depotStats, setDepotStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderFilters, setOrderFilters] = useState({
    status: '',
    from_date: '',
    to_date: ''
  });
  const [orderPagination, setOrderPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0
  });

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
  try {
    setLoading(true);
    
    // Fetch stats
    const statsResponse = await api.get('/sales/dashboard/stats');
    setStats(statsResponse.data);

    // Fetch recent transactions
    const transactionsResponse = await api.get('/sales/transactions', {
      params: { per_page: 5 }
    });
    setRecentTransactions(transactionsResponse.data.items || []);

    // Fetch sales chart data for the last 7 days
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 6);
    
    const salesChartResponse = await api.get('/reports/sales', {
      params: {
        from_date: format(startDate, 'yyyy-MM-dd'),
        to_date: format(endDate, 'yyyy-MM-dd'),
        format: 'json'
      }
    });
    
    // Create array of all 7 days
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      last7Days.push({
        date: format(date, 'dd MMM'),
        fullDate: format(date, 'yyyy-MM-dd'),
        amount: 0
      });
    }
    
    // Fill in sales data
    if (salesChartResponse.data.data) {
      const salesDataMap = {};
      salesChartResponse.data.data.forEach(item => {
        salesDataMap[item.Date] = (salesDataMap[item.Date] || 0) + item.Amount;
      });
      
      const filledData = last7Days.map(day => ({
        ...day,
        amount: salesDataMap[day.fullDate] || 0
      }));
      
      setSalesData(filledData);
    } else {
      setSalesData(last7Days);
    }

    // Fetch pending approvals
    const pendingResponse = await api.get('/expenses/', {
      params: { approved: 'false', per_page: 5 }
    });
    setPendingApprovals(pendingResponse.data.items || []);

    // Fetch recent consignments
    const consignmentsResponse = await api.get('/consignments/', {
      params: { per_page: 5, status: 'issued' }
    });
    setRecentConsignments(consignmentsResponse.data.items || []);

    // Fetch orders for dashboard stats
    await fetchOrdersForStats();

    // Fetch depot stats (for manager)
    if (user?.user_type === 'manager') {
      const depotStatsResponse = await api.get('/depot/stats');
      setDepotStats(depotStatsResponse.data || {});
    }

  } catch (error) {
    console.error('Failed to fetch dashboard data:', error);
  } finally {
    setLoading(false);
  }
};

const fetchOrdersForStats = async () => {
  try {
    const params = { 
      per_page: 100,
      status: 'pending,processing'
    };
    
    // Add role-specific parameters
    if (user?.user_type === 'sales_agent') {
      params.assigned_to_agent = true;
    } else if (user?.user_type === 'shop_owner') {
      params.my_orders_only = true;
    } else if (user?.user_type === 'manager') {
      params.by_depot = true;
    }
    
    const ordersResponse = await api.get('/orders/', { params });
    setOrders(ordersResponse.data.items || []);
  } catch (error) {
    console.error('Failed to fetch orders:', error);
  }
};

 const fetchAllOrders = async () => {
  try {
    setOrdersLoading(true);
    
    const params = {
      page: orderPagination.page,
      per_page: orderPagination.per_page,
      status: orderFilters.status || undefined,
      from_date: orderFilters.from_date || undefined,
      to_date: orderFilters.to_date || undefined
    };
    
    // Add role-specific parameters
    if (user?.user_type === 'sales_agent') {
      params.assigned_to_agent = true;
    } else if (user?.user_type === 'shop_owner') {
      params.my_orders_only = true;
    } else if (user?.user_type === 'manager') {
      params.by_depot = true;
    }
    
    const response = await api.get('/orders/', { params });
    setOrders(response.data.items || []);
    setOrderPagination(prev => ({
      ...prev,
      total: response.data.total
    }));
  } catch (error) {
    console.error('Failed to fetch orders:', error);
  } finally {
    setOrdersLoading(false);
  }
};

  const handleOpenOrdersModal = () => {
    setOrderPagination({ page: 1, per_page: 10, total: 0 });
    fetchAllOrders();
    setShowOrdersModal(true);
  };

  // Orders Table Columns
  const ordersColumns = [
    {
      header: 'Order #',
      accessorKey: 'order_number',
      cell: ({ getValue }) => (
        <span className="font-medium text-primary-600">{getValue()}</span>
      )
    },
    {
      header: 'Date',
      accessorKey: 'order_date',
      cell: ({ getValue }) => getValue() ? format(new Date(getValue()), 'dd MMM yyyy') : '-'
    },
    {
      header: 'Customer',
      accessorKey: 'shop_owner_name',
      cell: ({ getValue }) => (
        <div>
          <div className="font-medium">{getValue()}</div>
        </div>
      )
    },
    {
      header: 'Items',
      accessorKey: 'items_count',
      cell: ({ getValue }) => `${getValue()} items`
    },
    {
      header: 'Total',
      accessorKey: 'total_amount',
      cell: ({ getValue }) => `ZMW ${getValue()?.toLocaleString() || 0}`
    },
    {
      header: 'Paid',
      accessorKey: 'amount_paid',
      cell: ({ getValue }) => `ZMW ${getValue()?.toLocaleString() || 0}`
    },
    {
      header: 'Balance',
      accessorKey: 'balance',
      cell: ({ getValue }) => (
        <span className={getValue() > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
          ZMW ${getValue()?.toLocaleString() || 0}
        </span>
      )
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ getValue }) => <StatusBadge status={getValue()} />
    },
    {
      header: 'Delivery',
      accessorKey: 'delivery_date',
      cell: ({ getValue }) => getValue() ? format(new Date(getValue()), 'dd MMM yyyy') : 'Not set'
    }
  ];

  const undeliveredOrdersCount = orders.filter(o => o.status === 'pending' || o.status === 'processing').length;

  // Owner Dashboard
  const OwnerDashboard = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Today&apos;s Sales
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ZMW {stats.total_sales_today?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <ShoppingCartIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Monthly Sales
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ZMW {stats.total_sales_month?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-red-500 rounded-md p-3">
                <ExclamationCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Outstanding Credit
                  </dt>
                  <dd className="text-lg font-medium text-red-600">
                    ZMW {stats.outstanding_credit?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                <UserGroupIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active Customers
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.total_shop_owners || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-orange-500 rounded-md p-3">
                <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Pending Orders
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {undeliveredOrdersCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Trend */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sales Trend (Last 7 Days)</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value) => [`ZMW ${value.toLocaleString()}`, 'Sales']} />
                <Area type="monotone" dataKey="amount" stroke="#3b82f6" fill="#93c5fd" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pending Orders Card */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Pending Orders</h3>
            <button
              onClick={handleOpenOrdersModal}
              className="text-sm text-primary-600 hover:text-primary-800 flex items-center"
            >
              View All <ArrowPathIcon className="h-4 w-4 ml-1" />
            </button>
          </div>
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {orders.length === 0 ? (
                <li className="py-4 text-center text-gray-500">No pending orders</li>
              ) : (
                orders.slice(0, 5).map((order) => (
                  <li key={order.id} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <ClipboardDocumentListIcon className="h-8 w-8 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {order.order_number}
                        </p>
                        <p className="text-sm text-gray-500">
                          {order.shop_owner_name} • {order.order_date}
                        </p>
                      </div>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        ZMW {order.total_amount?.toLocaleString()}
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
          {orders.length > 0 && (
            <div className="mt-6">
              <button
                onClick={handleOpenOrdersModal}
                className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                View all orders
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Second row - Recent Transactions and Recent Consignments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Transactions</h3>
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {recentTransactions.length === 0 ? (
                <li className="py-4 text-center text-gray-500">No recent transactions</li>
              ) : (
                recentTransactions.map((transaction) => (
                  <li key={transaction.id} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {transaction.shop_owner_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {transaction.transaction_number} &bull; {transaction.transaction_date}
                        </p>
                      </div>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.status === 'paid' ? 'bg-green-100 text-green-800' :
                          transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          transaction.status === 'overdue' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.status}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        ZMW {transaction.total_amount?.toLocaleString()}
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
          <div className="mt-6">
            <a
              href="/sales"
              className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              View all transactions
            </a>
          </div>
        </div>

        {/* Recent Consignments */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Active Consignments</h3>
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {recentConsignments.length === 0 ? (
                <li className="py-4 text-center text-gray-500">No active consignments</li>
              ) : (
                recentConsignments.map((consignment) => (
                  <li key={consignment.id} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <TruckIcon className="h-8 w-8 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {consignment.consignment_number}
                        </p>
                        <p className="text-sm text-gray-500">
                          {consignment.sales_agent_name} • {consignment.destination_town}
                        </p>
                      </div>
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {consignment.items_count} items
                        </span>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
          <div className="mt-6">
            <a
              href="/consignments"
              className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              View all consignments
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  // Sales Agent Dashboard (Updated with Orders)
  const SalesAgentDashboard = () => {
    const agentOrders = orders.filter(o => o.sales_agent_name === user?.full_name);
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Today&apos;s Sales</dt>
                    <dd className="text-lg font-medium text-gray-900">ZMW {stats.sales_today?.toLocaleString() || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                  <TruckIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Active Consignments</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.active_consignment || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Today&apos;s Collections</dt>
                    <dd className="text-lg font-medium text-gray-900">ZMW {stats.collections_today?.toLocaleString() || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-orange-500 rounded-md p-3">
                  <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending Orders</dt>
                    <dd className="text-lg font-medium text-gray-900">{agentOrders.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Plan */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
  Today&apos;s Plan
</h3>
          {stats.active_consignment > 0 ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <span className="font-medium">You have {stats.active_consignment} active consignment(s)</span>
                </p>
              </div>
              <div className="flex space-x-3">
                <a
                  href="/consignments"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  View Consignments
                </a>
                {agentOrders.length > 0 && (
                  <button
                    onClick={handleOpenOrdersModal}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    View Orders ({agentOrders.length})
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No consignment for today</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new consignment.</p>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {recentTransactions.slice(0, 3).map((transaction) => (
                <li key={transaction.id} className="py-3">
                  <div className="flex items-center space-x-3">
                    <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                      transaction.status === 'paid' ? 'bg-green-100' :
                      transaction.status === 'pending' ? 'bg-yellow-100' : 'bg-gray-100'
                    }`}>
                      {transaction.status === 'paid' ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      ) : transaction.status === 'pending' ? (
                        <ClockIcon className="h-5 w-5 text-yellow-600" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {transaction.shop_owner_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {transaction.transaction_number}
                      </p>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      ZMW {transaction.total_amount?.toLocaleString()}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Shop Owner Dashboard (Updated with Orders)
  const ShopOwnerDashboard = () => {
    const shopOrders = orders.filter(o => o.shop_owner_name === user?.profile?.business_name);
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-red-500 rounded-md p-3">
                  <CurrencyDollarIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Current Balance</dt>
                    <dd className="text-lg font-medium text-red-600">ZMW {stats.current_balance?.toLocaleString() || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                  <ChartBarIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Credit Limit</dt>
                    <dd className="text-lg font-medium text-gray-900">ZMW {stats.credit_limit?.toLocaleString() || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                  <ChartBarIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Credit Utilization</dt>
                    <dd className="text-lg font-medium text-gray-900">{stats.credit_utilization?.toFixed(1) || 0}%</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-orange-500 rounded-md p-3">
                  <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">My Orders</dt>
                    <dd className="text-lg font-medium text-gray-900">{shopOrders.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Credit Progress */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Credit Usage</h3>
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-primary-600 bg-primary-200">
                  Credit Used
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-primary-600">
                  {stats.credit_utilization?.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-primary-200">
              <div
                style={{ width: `${Math.min(stats.credit_utilization || 0, 100)}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary-500"
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Used: ZMW {(stats.current_balance || 0).toLocaleString()}</span>
              <span>Limit: ZMW {(stats.credit_limit || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        {shopOrders.length > 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">My Recent Orders</h3>
              <button
                onClick={handleOpenOrdersModal}
                className="text-sm text-primary-600 hover:text-primary-800"
              >
                View All
              </button>
            </div>
            <div className="flow-root">
              <ul className="-my-5 divide-y divide-gray-200">
                {shopOrders.slice(0, 5).map((order) => (
                  <li key={order.id} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {order.order_number}
                        </p>
                        <p className="text-sm text-gray-500">
                          {order.order_date} • {order.items_count} items
                        </p>
                      </div>
                      <div>
                        <StatusBadge status={order.status} />
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        ZMW {order.total_amount?.toLocaleString()}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Recent Purchases */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Purchases</h3>
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {recentTransactions.length === 0 ? (
                <li className="py-4 text-center text-gray-500">No recent purchases</li>
              ) : (
                recentTransactions.map((transaction) => (
                  <li key={transaction.id} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {transaction.transaction_number}
                        </p>
                        <p className="text-sm text-gray-500">
                          {transaction.transaction_date}
                        </p>
                      </div>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.status === 'paid' ? 'bg-green-100 text-green-800' :
                          transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          transaction.status === 'overdue' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.status}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        ZMW {transaction.total_amount?.toLocaleString()}
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Manager Dashboard (Updated with Orders)
  const ManagerDashboard = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                <TruckIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active Consignments
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.pending_consignments || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Today&apos;s Sales
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ZMW {stats.today_sales?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
                <ExclamationCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Damaged Goods
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.damaged_goods || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                <UserGroupIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Shop Owners
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {stats.shop_owners || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-orange-500 rounded-md p-3">
                <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Pending Orders
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {undeliveredOrdersCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Depot Stats - Additional row for managers */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                <ArchiveBoxIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Inventory Value
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    ZMW {depotStats.total_inventory_value?.toLocaleString() || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-pink-500 rounded-md p-3">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Pending Approvals
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {pendingApprovals.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-teal-500 rounded-md p-3">
                <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active Orders
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {undeliveredOrdersCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Consignments */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Active Consignments</h3>
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {recentConsignments.length === 0 ? (
                <li className="py-4 text-center text-gray-500">No active consignments</li>
              ) : (
                recentConsignments.map((consignment) => (
                  <li key={consignment.id} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <TruckIcon className="h-8 w-8 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {consignment.consignment_number}
                        </p>
                        <p className="text-sm text-gray-500">
                          {consignment.sales_agent_name} • {consignment.destination_town}
                        </p>
                      </div>
                      <div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {consignment.items_count} items
                        </span>
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
          <div className="mt-6">
            <a
              href="/consignments"
              className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              View all consignments
            </a>
          </div>
        </div>

        {/* Pending Orders */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Pending Orders</h3>
            <button
              onClick={handleOpenOrdersModal}
              className="text-sm text-primary-600 hover:text-primary-800"
            >
              View All
            </button>
          </div>
          <div className="flow-root">
            <ul className="-my-5 divide-y divide-gray-200">
              {orders.length === 0 ? (
                <li className="py-4 text-center text-gray-500">No pending orders</li>
              ) : (
                orders.slice(0, 5).map((order) => (
                  <li key={order.id} className="py-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <ClipboardDocumentListIcon className="h-8 w-8 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {order.order_number}
                        </p>
                        <p className="text-sm text-gray-500">
                          {order.shop_owner_name} • {order.order_date}
                        </p>
                      </div>
                      <div>
                        <StatusBadge status={order.status} />
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        ZMW {order.total_amount?.toLocaleString()}
                      </div>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Transactions</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
               </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentTransactions.slice(0, 5).map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {transaction.transaction_date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {transaction.transaction_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {transaction.shop_owner_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ZMW {transaction.total_amount?.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      transaction.status === 'paid' ? 'bg-green-100 text-green-800' :
                      transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      transaction.status === 'overdue' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {transaction.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Low Stock Alert (conditional) */}
      {stats.low_stock_items > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <span className="font-medium">Low Stock Alert!</span> {stats.low_stock_items} products are running low on stock.
                <a href="/inventory" className="ml-2 font-medium underline text-yellow-700 hover:text-yellow-600">
                  View Inventory
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">
          Welcome back, {user?.first_name}!
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {format(new Date(), 'EEEE, MMMM do, yyyy')}
        </p>
      </div>

      {user?.user_type === 'owner' && <OwnerDashboard />}
      {user?.user_type === 'sales_agent' && <SalesAgentDashboard />}
      {user?.user_type === 'shop_owner' && <ShopOwnerDashboard />}
      {user?.user_type === 'manager' && <ManagerDashboard />}

      {/* Orders Modal */}
      <Modal
        isOpen={showOrdersModal}
        onClose={() => {
          setShowOrdersModal(false);
          setOrderFilters({ status: '', from_date: '', to_date: '' });
          setOrderPagination({ page: 1, per_page: 10, total: 0 });
        }}
        title="All Orders"
        size="xl"
      >
        <div className="space-y-4">
          {/* Order Filters */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={orderFilters.status}
                onChange={(e) => {
                  setOrderFilters({...orderFilters, status: e.target.value});
                  setOrderPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">From Date</label>
              <input
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={orderFilters.from_date}
                onChange={(e) => {
                  setOrderFilters({...orderFilters, from_date: e.target.value});
                  setOrderPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">To Date</label>
              <input
                type="date"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={orderFilters.to_date}
                onChange={(e) => {
                  setOrderFilters({...orderFilters, to_date: e.target.value});
                  setOrderPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
            </div>
          </div>
          
          <button
            onClick={() => fetchAllOrders()}
            className="mb-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
          >
            Apply Filters
          </button>

          {/* Orders Table */}
          <DataTable
            data={orders}
            columns={ordersColumns}
            loading={ordersLoading}
            pageSize={orderPagination.per_page}
            total={orderPagination.total}
            page={orderPagination.page}
            onPageChange={(page) => setOrderPagination(prev => ({ ...prev, page }))}
            onPageSizeChange={(per_page) => setOrderPagination(prev => ({ ...prev, per_page, page: 1 }))}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;